import React, { useEffect, useMemo, useState } from 'react'
import { useI18n } from '../../../i18n'
import { SearchField } from '../../ui/SearchField'
import { Button } from '../../ui/Button'
import { GovernancePickerModal } from '../../governance/GovernancePickerModal'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'

export type LibraryEvidenceRow = {
  id: string
  displayId?: string
  title?: string
  verificationStatus?: string
  freshnessStatus?: string
  mappedControls?: string[]
  mappedStandards?: string[]
  crossStandardReuse?: boolean
  suggestionReason?: string
}

interface Props {
  open: boolean
  onClose: () => void
  suggested: LibraryEvidenceRow[]
  library: LibraryEvidenceRow[]
  linkedIds: Set<string>
  onConfirmLink: (evidenceIds: string[], linkageSource: 'MANUAL' | 'AUTO_SUGGESTED' | 'AUTO_REUSED') => void | Promise<void>
  reuseCandidates?: Array<{
    evidenceId: string
    reusableFrom: string
    targetStandard: string
    reason?: string
  }>
  loading?: boolean
  loadError?: string | null
}

export function QuestionEvidenceLibraryPicker({
  open,
  onClose,
  suggested,
  library,
  linkedIds,
  onConfirmLink,
  reuseCandidates = [],
  loading,
  loadError,
}: Props) {
  const { t } = useI18n()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    setSearch('')
    setSelected(new Set())
    setBusy(false)
  }, [open])

  const filteredLibrary = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return library
    return library.filter(
      (r) =>
        String(r.title || '').toLowerCase().includes(q) ||
        String(r.displayId || '').toLowerCase().includes(q) ||
        (r.mappedControls || []).some((c) => c.toLowerCase().includes(q)),
    )
  }, [library, search])

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const renderRow = (row: LibraryEvidenceRow, source: 'AUTO_SUGGESTED' | 'MANUAL') => {
    const checked = selected.has(row.id)
    const already = linkedIds.has(row.id)
    const rowClass = [
      listStyles.pickerRow,
      !already ? listStyles.pickerRowSelectable : '',
      already ? listStyles.pickerRowDisabled : '',
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <li key={row.id}>
        <label className={rowClass}>
          <span className={listStyles.pickerRowMain}>
            <input
              type="checkbox"
              className="mt-1 shrink-0"
              disabled={already || busy}
              checked={already ? true : checked}
              onChange={() => {
                if (!already && !busy) toggle(row.id)
              }}
            />
            <span className="min-w-0 flex-1">
              <p className={listStyles.pickerRowTitle}>{row.title || row.displayId}</p>
              {row.displayId ? (
                <p className={`${listStyles.pickerRowMeta} ${listStyles.pickerRowMono}`}>{row.displayId}</p>
              ) : null}
              <p className={listStyles.pickerRowMeta}>
                {[row.verificationStatus, row.freshnessStatus].filter(Boolean).join(' · ')}
                {row.mappedControls?.length ? ` · ${row.mappedControls.join(', ')}` : ''}
              </p>
              {source === 'AUTO_SUGGESTED' && row.suggestionReason ? (
                <p className={`${listStyles.pickerRowMeta} text-emerald-800`}>{row.suggestionReason}</p>
              ) : null}
            </span>
          </span>
        </label>
      </li>
    )
  }

  const confirm = async (linkageSource: 'MANUAL' | 'AUTO_SUGGESTED' | 'AUTO_REUSED') => {
    const ids = [...selected].filter((id) => !linkedIds.has(id))
    if (!ids.length) return
    setBusy(true)
    try {
      await onConfirmLink(ids, linkageSource)
      setSelected(new Set())
      onClose()
    } finally {
      setBusy(false)
    }
  }

  const footer = (
    <>
      <Button type="button" size="small" color="outline" onClick={onClose} disabled={busy}>
        {t('common.cancel', 'Cancel')}
      </Button>
      <Button
        type="button"
        size="small"
        color="primary"
        disabled={busy || !selected.size}
        onClick={() => void confirm('MANUAL')}
      >
        {t('complianceEvidence.linkSelected', 'Link selected')}
      </Button>
    </>
  )

  return (
    <GovernancePickerModal
      open={open}
      onClose={onClose}
      title={t('complianceEvidence.libraryTitle', 'Evidence library')}
      footer={footer}
      titleId="question-evidence-library-modal-title"
    >
      {loading ? <p className={listStyles.pickerRowMeta}>{t('common.loading', 'Loading…')}</p> : null}
      {loadError ? <p className={listStyles.formError}>{loadError}</p> : null}

      {suggested.length ? (
        <section className={listStyles.pickerSection}>
          <h4 className={listStyles.pickerSectionTitle}>
            {t('complianceEvidence.suggested', 'Suggested evidence')}
          </h4>
          <ul className={listStyles.pickerList}>{suggested.map((r) => renderRow(r, 'AUTO_SUGGESTED'))}</ul>
          <Button
            type="button"
            size="small"
            color="outline"
            disabled={busy || !selected.size}
            onClick={() => void confirm('AUTO_SUGGESTED')}
          >
            {t('complianceEvidence.linkSuggested', 'Link selected suggestions')}
          </Button>
        </section>
      ) : null}

      {reuseCandidates.length ? (
        <section className={listStyles.pickerBannerWarning}>
          <h4 className={listStyles.pickerSectionTitle}>{t('complianceEvidence.reuseTitle', 'Reusable from')}</h4>
          <ul className={`${listStyles.pickerList} mt-2`}>
            {reuseCandidates.map((r) => (
              <li key={`${r.evidenceId}-${r.reusableFrom}`} className={listStyles.pickerRowMeta}>
                <span className="font-medium text-slate-900">{r.reusableFrom}</span>
                {r.reason ? ` — ${r.reason}` : ''}
                <Button
                  type="button"
                  size="small"
                  color="outline"
                  className="ml-2"
                  onClick={() => void onConfirmLink([r.evidenceId], 'AUTO_REUSED')}
                >
                  {t('complianceEvidence.reuseLink', 'Reuse reference')}
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className={listStyles.pickerSection}>
        <SearchField
          value={search}
          onChange={setSearch}
          placeholder={t('complianceEvidence.searchLibrary', 'Search evidence…')}
          minWidth="100%"
        />
        {!loading && !filteredLibrary.length ? (
          <p className={listStyles.pickerEmpty}>
            {t(
              'complianceEvidence.libraryEmpty',
              'No evidence items found. Upload items in Evidence Hub first.',
            )}
          </p>
        ) : (
          <ul className={listStyles.pickerList}>{filteredLibrary.map((r) => renderRow(r, 'MANUAL'))}</ul>
        )}
      </section>
    </GovernancePickerModal>
  )
}
